import React from 'react';
import './styles.css';

// Base Icon Component
const Icon = ({ name, size = 24, className = '', ...props }) => {
  const iconMap = {
    // Vault & Security Icons
    'vault': VaultIcon,
    'lock': LockIcon,
    'shield': ShieldIcon,
    'key': KeyIcon,
    'safe': SafeIcon,

    // Memory & Family Icons
    'memory': MemoryIcon,
    'family-tree': FamilyTreeIcon,
    'photo': PhotoIcon,
    'document': DocumentIcon,
    'story': StoryIcon,

    // Features Icons
    'upload': UploadIcon,
    'download': DownloadIcon,
    'share': ShareIcon,
    'privacy': PrivacyIcon,
    'emergency': EmergencyIcon,
    'gallery': GalleryIcon,

    // Navigation Icons
    'dashboard': DashboardIcon,
    'settings': SettingsIcon,
    'user': UserIcon,
    'users': UsersIcon,
    'bell': BellIcon,
    'search': SearchIcon,
    'menu': MenuIcon,
    'close': CloseIcon,
    'x': CloseIcon,
    'arrow-right': ArrowRightIcon,
    'arrow-down': ArrowDownIcon,
    'arrow-up': ArrowUpIcon,
    'chevron-left': ChevronLeftIcon,
    'chevron-right': ChevronRightIcon,
    'chevron-down': ChevronDownIcon,
    'chevron-up': ChevronUpIcon,

    // Status Icons
    'check': CheckIcon,
    'check-circle': CheckCircleIcon,
    'plus': PlusIcon,
    'edit': EditIcon,
    'delete': DeleteIcon,
    'heart': HeartIcon,
    'star': StarIcon,
    'fire': FireIcon,
    'cookie': CookieIcon,
    'save': SaveIcon,

    // Custodial Icons
    'palette': PaletteIcon,
    'api': ApiIcon,
    'headphones': HeadphonesIcon,
    'trending-up': TrendingUpIcon,
    'scale': ScaleIcon,
    'briefcase': BriefcaseIcon,
    'building': BuildingIcon,
    'calendar': CalendarIcon,
    'file-text': FileTextIcon,

    // Media Icons
    'play': PlayIcon,
    'play-circle': PlayCircleIcon,
    'clock': ClockIcon,
    'loader': LoaderIcon,
    'grid': GridIcon,
    'copy': CopyIcon,
    'refresh': RefreshIcon,
    'help-circle': HelpCircleIcon,
    'git-branch': GitBranchIcon,
  };

  const IconComponent = iconMap[name];

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found`);
    return null;
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={`eterna-icon ${className}`}
      {...props}
    >
      <IconComponent />
    </svg>
  );
};

// Vault & Security Icons
const VaultIcon = () => (
  <g>
    {/* Gold wire vault body with decorative corners */}
    <rect x="4" y="6" width="16" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Decorative corner flourishes */}
    <path d="M4 6 L6 4 L8 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 6 L18 4 L20 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M4 18 L6 20 L8 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 18 L18 20 L20 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    {/* Ornate lock mechanism */}
    <circle cx="12" cy="12" r="3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    {/* Decorative lock shackle */}
    <path d="M8 6V3a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Wire texture lines */}
    <path d="M6 9 L18 9" stroke="currentColor" strokeWidth="1" opacity="0.6" />
    <path d="M6 15 L18 15" stroke="currentColor" strokeWidth="1" opacity="0.6" />
  </g>
);

const LockIcon = () => (
  <g>
    {/* Gold wire lock body with ornate details */}
    <rect x="3" y="11" width="18" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Decorative lock shackle with wire texture */}
    <path d="M7 11V6a5 5 0 0 1 10 0v5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Ornate keyhole */}
    <circle cx="12" cy="16" r="1.5" fill="currentColor" />
    <rect x="11.5" y="16" width="1" height="3" fill="currentColor" />
    {/* Wire texture details */}
    <path d="M5 13 L19 13" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    <path d="M5 19 L19 19" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    {/* Decorative corner flourishes */}
    <path d="M3 11 L5 9 L7 11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M17 11 L19 9 L21 11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </g>
);

const ShieldIcon = () => (
  <g>
    {/* Ornate gold wire shield with decorative elements */}
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Decorative wire patterns inside shield */}
    <path d="M12 5 L12 15" stroke="currentColor" strokeWidth="1.5" opacity="0.7" />
    <path d="M8 8 L16 8" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    <path d="M8 12 L16 12" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    {/* Ornate checkmark with wire flourish */}
    <path d="M9 12l2 2 4-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    {/* Decorative corner flourishes */}
    <path d="M4 5 L6 3 L8 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M16 5 L18 3 L20 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    {/* Wire texture lines */}
    <path d="M6 7 L18 7" stroke="currentColor" strokeWidth="0.8" opacity="0.4" />
    <path d="M6 10 L18 10" stroke="currentColor" strokeWidth="0.8" opacity="0.4" />
  </g>
);

const KeyIcon = () => (
  <g>
    {/* Ornate gold wire key with decorative elements */}
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    {/* Decorative wire flourishes on key head */}
    <circle cx="12" cy="12" r="1" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
    <path d="M10 10 L14 14" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    <path d="M14 10 L10 14" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    {/* Wire texture on key shaft */}
    <path d="M15.5 7.5 L19 4" stroke="currentColor" strokeWidth="1" opacity="0.4" />
  </g>
);

const SafeIcon = () => (
  <g>
    {/* Ornate gold wire safe with decorative elements */}
    <rect x="3" y="4" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <rect x="7" y="8" width="10" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Ornate combination lock */}
    <circle cx="12" cy="12" r="2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
    {/* Decorative safe handles */}
    <path d="M6 2v2M18 2v2" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Wire texture details */}
    <path d="M5 6 L19 6" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    <path d="M5 10 L19 10" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    <path d="M5 14 L19 14" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    <path d="M5 18 L19 18" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    {/* Decorative corner flourishes */}
    <path d="M3 4 L5 2 L7 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M17 4 L19 2 L21 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M3 20 L5 22 L7 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M17 20 L19 22 L21 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </g>
);

// Memory & Family Icons
const MemoryIcon = () => (
  <g>
    {/* Ornate gold wire memory frame */}
    <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Decorative inner frame */}
    <path d="M9 9h6v6H9z" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Ornate corner decorations */}
    <circle cx="9" cy="9" r="1.5" fill="currentColor" />
    <circle cx="15" cy="9" r="1.5" fill="currentColor" />
    <circle cx="9" cy="15" r="1.5" fill="currentColor" />
    <circle cx="15" cy="15" r="1.5" fill="currentColor" />
    {/* Wire texture lines */}
    <path d="M6 6 L18 6" stroke="currentColor" strokeWidth="1" opacity="0.4" />
    <path d="M6 12 L18 12" stroke="currentColor" strokeWidth="1" opacity="0.4" />
    <path d="M6 18 L18 18" stroke="currentColor" strokeWidth="1" opacity="0.4" />
    <path d="M6 6 L6 18" stroke="currentColor" strokeWidth="1" opacity="0.4" />
    <path d="M12 6 L12 18" stroke="currentColor" strokeWidth="1" opacity="0.4" />
    <path d="M18 6 L18 18" stroke="currentColor" strokeWidth="1" opacity="0.4" />
    {/* Decorative corner flourishes */}
    <path d="M3 3 L5 1 L7 3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M17 3 L19 1 L21 3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M3 21 L5 23 L7 21" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M17 21 L19 23 L21 21" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </g>
);

const FamilyTreeIcon = () => (
  <g>
    {/* Ornate gold wire family tree */}
    <path d="M12 2v4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="12" cy="2" r="2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Decorative wire connections */}
    <path d="M8 8v4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M16 8v4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="8" cy="8" r="2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="16" cy="8" r="2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Ornate family member nodes */}
    <path d="M6 16v4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M10 16v4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M14 16v4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M18 16v4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="6" cy="16" r="2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="10" cy="16" r="2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="14" cy="16" r="2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="18" cy="16" r="2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Decorative wire flourishes */}
    <path d="M12 6 L8 8" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
    <path d="M12 6 L16 8" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
    <path d="M8 12 L6 16" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
    <path d="M8 12 L10 16" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
    <path d="M16 12 L14 16" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
    <path d="M16 12 L18 16" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
  </g>
);

const PhotoIcon = () => (
  <g>
    <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="8.5" cy="8.5" r="1.5" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M21 15l-5-5L5 21" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const DocumentIcon = () => (
  <g>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M14 2v6h6" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M16 13H8" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M16 17H8" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M10 9H8" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const StoryIcon = () => (
  <g>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M8 7h8" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M8 11h8" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M8 15h5" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

// Feature Icons
const UploadIcon = () => (
  <g>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M17 8l-5-5-5 5" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M12 3v12" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const DownloadIcon = () => (
  <g>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M12 15V3" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const ShareIcon = () => (
  <g>
    <circle cx="18" cy="5" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="6" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="18" cy="19" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M8.59 13.51l6.83 3.98" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M15.41 6.51l-6.82 3.98" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const PrivacyIcon = () => (
  <g>
    <rect x="3" y="11" width="18" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M12 16v2" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M8 16h8" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const EmergencyIcon = () => (
  <g>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M12 6v6l4 2" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const GalleryIcon = () => (
  <g>
    <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="8.5" cy="8.5" r="1.5" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M21 15l-5-5L5 21" fill="none" stroke="currentColor" strokeWidth="2" />
    <rect x="14" y="6" width="4" height="4" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

// Navigation Icons
const DashboardIcon = () => (
  <g>
    <rect x="3" y="3" width="7" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
    <rect x="14" y="3" width="7" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
    <rect x="14" y="14" width="7" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
    <rect x="3" y="14" width="7" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const SettingsIcon = () => (
  <g>
    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const UserIcon = () => (
  <g>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const UsersIcon = () => (
  <g>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="9" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const BellIcon = () => (
  <g>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const SearchIcon = () => (
  <g>
    <circle cx="11" cy="11" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M21 21l-4.35-4.35" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const MenuIcon = () => (
  <g>
    <line x1="3" y1="6" x2="21" y2="6" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="3" y1="12" x2="21" y2="12" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="3" y1="18" x2="21" y2="18" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const CloseIcon = () => (
  <g>
    <line x1="18" y1="6" x2="6" y2="18" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="6" y1="6" x2="18" y2="18" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const ArrowRightIcon = () => (
  <g>
    <line x1="5" y1="12" x2="19" y2="12" fill="none" stroke="currentColor" strokeWidth="2" />
    <polyline points="12,5 19,12 12,19" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const ArrowDownIcon = () => (
  <g>
    <line x1="12" y1="5" x2="12" y2="19" fill="none" stroke="currentColor" strokeWidth="2" />
    <polyline points="19,12 12,19 5,12" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const ArrowUpIcon = () => (
  <g>
    <line x1="12" y1="19" x2="12" y2="5" fill="none" stroke="currentColor" strokeWidth="2" />
    <polyline points="5,12 12,5 19,12" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const ChevronLeftIcon = () => (
  <g>
    <polyline points="15,18 9,12 15,6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </g>
);

const ChevronRightIcon = () => (
  <g>
    <polyline points="9,18 15,12 9,6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </g>
);

const ChevronDownIcon = () => (
  <g>
    <polyline points="6,9 12,15 18,9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </g>
);

const ChevronUpIcon = () => (
  <g>
    <polyline points="18,15 12,9 6,15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </g>
);

// Status Icons
const CheckIcon = () => (
  <g>
    <polyline points="20,6 9,17 4,12" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const CheckCircleIcon = () => (
  <g>
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
    <polyline points="9,12 12,15 15,9" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const PlusIcon = () => (
  <g>
    <line x1="12" y1="5" x2="12" y2="19" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="5" y1="12" x2="19" y2="12" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const EditIcon = () => (
  <g>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const DeleteIcon = () => (
  <g>
    <polyline points="3,6 5,6 21,6" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="10" y1="11" x2="10" y2="17" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="14" y1="11" x2="14" y2="17" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const HeartIcon = () => (
  <g>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const StarIcon = () => (
  <g>
    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const FireIcon = () => (
  <g>
    {/* Main flame - left side */}
    <path d="M10 18 L8 14 L10 10 L12 8 L10 10 L8 12 L10 18 Z" fill="currentColor" opacity="0.9" />
    {/* Main flame - center */}
    <path d="M12 20 L10 16 L12 8 L14 16 L12 20 Z" fill="currentColor" opacity="0.95" />
    {/* Main flame - right side */}
    <path d="M14 18 L16 14 L14 10 L12 8 L14 10 L16 12 L14 18 Z" fill="currentColor" opacity="0.9" />
    {/* Inner flame core - left */}
    <path d="M11 16 L10 13 L11 11 L12 10 L11 11 L10 12 L11 16 Z" fill="currentColor" opacity="0.7" />
    {/* Inner flame core - center */}
    <path d="M12 18 L11 15 L12 10 L13 15 L12 18 Z" fill="currentColor" opacity="0.8" />
    {/* Inner flame core - right */}
    <path d="M13 16 L14 13 L13 11 L12 10 L13 11 L14 12 L13 16 Z" fill="currentColor" opacity="0.7" />
    {/* Base glow */}
    <ellipse cx="12" cy="20" rx="2.5" ry="1.5" fill="currentColor" opacity="0.5" />
  </g>
);

// Media Icons
const PlayIcon = () => (
  <g>
    <polygon points="5,3 19,12 5,21" fill="currentColor" />
  </g>
);

const PlayCircleIcon = () => (
  <g>
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
    <polygon points="10,8 16,12 10,16" fill="currentColor" />
  </g>
);

const ClockIcon = () => (
  <g>
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
    <polyline points="12,6 12,12 16,14" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const SaveIcon = () => (
  <g>
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" fill="none" stroke="currentColor" strokeWidth="2" />
    <polyline points="17,21 17,13 7,13 7,21" fill="none" stroke="currentColor" strokeWidth="2" />
    <polyline points="7,3 7,8 15,8" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const LoaderIcon = () => (
  <g>
    <line x1="12" y1="2" x2="12" y2="6" stroke="currentColor" strokeWidth="2" />
    <line x1="12" y1="18" x2="12" y2="22" stroke="currentColor" strokeWidth="2" />
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" stroke="currentColor" strokeWidth="2" />
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" stroke="currentColor" strokeWidth="2" />
    <line x1="2" y1="12" x2="6" y2="12" stroke="currentColor" strokeWidth="2" />
    <line x1="18" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="2" />
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" stroke="currentColor" strokeWidth="2" />
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" stroke="currentColor" strokeWidth="2" />
  </g>
);

const GridIcon = () => (
  <g>
    <rect x="3" y="3" width="7" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
    <rect x="14" y="3" width="7" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
    <rect x="14" y="14" width="7" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
    <rect x="3" y="14" width="7" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const RefreshIcon = () => (
  <g>
    <path d="M23 4v6h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1 20v-6h6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </g>
);

const CopyIcon = () => (
  <g>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const CookieIcon = () => (
  <g>
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="8" cy="8" r="1" fill="currentColor" />
    <circle cx="16" cy="8" r="1" fill="currentColor" />
    <circle cx="8" cy="16" r="1" fill="currentColor" />
    <circle cx="16" cy="16" r="1" fill="currentColor" />
    <circle cx="12" cy="6" r="1" fill="currentColor" />
    <circle cx="12" cy="18" r="1" fill="currentColor" />
    <circle cx="6" cy="12" r="1" fill="currentColor" />
    <circle cx="18" cy="12" r="1" fill="currentColor" />
  </g>
);

// Custodial Icons
const PaletteIcon = () => (
  <g>
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
    <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
    <circle cx="12.5" cy="13.5" r=".5" fill="currentColor" />
    <circle cx="16.5" cy="6.5" r=".5" fill="currentColor" />
    <circle cx="9.5" cy="16.5" r=".5" fill="currentColor" />
    <circle cx="15.5" cy="15.5" r=".5" fill="currentColor" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const ApiIcon = () => (
  <g>
    <path d="M4 9h16" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M4 15h16" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M10 3L6 21" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M18 3l-4 18" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const HeadphonesIcon = () => (
  <g>
    <path d="M3 14v3a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M21 14v3a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h1a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M12 2a7 7 0 0 0-7 7v1" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M12 2a7 7 0 0 1 7 7v1" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const TrendingUpIcon = () => (
  <g>
    <polyline points="23,6 13.5,15.5 8.5,10.5 1,18" fill="none" stroke="currentColor" strokeWidth="2" />
    <polyline points="17,6 23,6 23,12" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const ScaleIcon = () => (
  <g>
    <path d="M16 16l3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1z" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M2 16l3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1z" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M7 21h10" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M12 3v18" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const BriefcaseIcon = () => (
  <g>
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const BuildingIcon = () => (
  <g>
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M6 12H4a2 2 0 0 0-2 2v8h20v-8a2 2 0 0 0-2-2h-2" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M18 9v3" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M13 9v3" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M9 9v3" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M9 19v3" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M13 19v3" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const CalendarIcon = () => (
  <g>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="16" y1="2" x2="16" y2="6" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="8" y1="2" x2="8" y2="6" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="3" y1="10" x2="21" y2="10" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const FileTextIcon = () => (
  <g>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" strokeWidth="2" />
    <polyline points="14,2 14,8 20,8" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="16" y1="13" x2="8" y2="13" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="16" y1="17" x2="8" y2="17" fill="none" stroke="currentColor" strokeWidth="2" />
    <polyline points="10,9 9,9 8,9" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

const HelpCircleIcon = () => (
  <g>
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="17" r="1" fill="currentColor" />
  </g>
);

const GitBranchIcon = () => (
  <g>
    <circle cx="6" cy="5" r="2" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="18" cy="5" r="2" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="6" cy="19" r="2" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="18" cy="19" r="2" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M6 7v10M18 7v10" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M8 5h10M8 19h10" fill="none" stroke="currentColor" strokeWidth="2" />
  </g>
);

export default Icon;
